import { ChangeEvent, memo, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Pagination as MuiPagination, PaginationItem, Skeleton } from '@material-ui/lab';
import clsx from 'clsx';

import { useStyles } from './Pagination.styles';
import { PaginationProps } from './Pagination.types';

export const Pagination = memo(
  ({
    loading,
    pagination,
    queryParams = { update: true, name: 'page' },
    classes,
    onChangePage,
    ...props
  }: PaginationProps) => {
    const styles = useStyles();

    const [searchParams, setSearchParams] = useSearchParams();

    const [currentPage, setCurrentPage] = useState(pagination?.currentPage || 1);

    const totalPages = useMemo(() => pagination?.totalPages || 1, [pagination?.totalPages]);

    const changePage = useCallback(
      (page: number) => {
        setCurrentPage(page);

        if (queryParams?.update) {
          searchParams.set(queryParams?.name ?? 'page', String(page));
          setSearchParams(searchParams, { replace: true });
        }
      },
      [searchParams, queryParams?.update, queryParams?.name, setSearchParams],
    );

    const handleChangePage = useCallback(
      (_: ChangeEvent<unknown>, page: number) => {
        changePage(page);

        if (onChangePage) {
          onChangePage(page);
        }
      },
      [changePage, onChangePage],
    );

    useLayoutEffect(() => {
      if (currentPage > totalPages) {
        changePage(totalPages);
      }
    }, [currentPage, totalPages]);

    if (!pagination || pagination.totalPages <= 1) {
      return null;
    }

    return (
      <div className={clsx(styles.root, classes?.root)}>
        {!loading && totalPages > 1 && (
          <MuiPagination
            {...props}
            page={currentPage}
            count={totalPages}
            color="primary"
            renderItem={item => <PaginationItem className={styles.paginationItem} {...item} />}
            classes={classes?.pagination}
            onChange={handleChangePage}
          />
        )}

        {loading && (
          <div className={styles.skeletonRoot}>
            <Skeleton variant="circle" width={20} height={20} />
            <Skeleton variant="circle" width={32} height={32} />
            <Skeleton variant="circle" width={32} height={32} />
            <Skeleton variant="circle" width={32} height={32} />
            <Skeleton variant="circle" width={20} height={20} />
          </div>
        )}
      </div>
    );
  },
);
